import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, MessageCircle, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { LOGO_URL, WHATSAPP, INSTAGRAM } from "../lib/api";
import api from "../lib/api";

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data.message);
      setDone(true);
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return <p className="text-sm text-accent">You're in! Welcome to the family.</p>;
  }

  return (
    <form onSubmit={submit} className="flex gap-0 mt-3" data-testid="newsletter-form">
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Your email"
        required
        className="flex-1 min-w-0 bg-primary-foreground/10 px-3 py-2.5 text-sm text-primary-foreground placeholder:text-primary-foreground/40 outline-none focus:bg-primary-foreground/15"
        data-testid="newsletter-email-input"
      />
      <button
        type="submit"
        disabled={loading}
        className="bg-accent text-accent-foreground px-4 py-2.5 text-sm flex items-center gap-1.5 disabled:opacity-50"
        data-testid="newsletter-submit-btn"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
      </button>
    </form>
  );
}

export default function Footer() {
  return (
    <footer className="mt-32 bg-primary text-primary-foreground" data-testid="footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <img src={LOGO_URL} alt="Aethel Decor — Beyond Decor. Simply Elevated." className="h-16 w-auto" />
          <p className="mt-6 font-display italic text-xl text-primary-foreground/90">
            Beyond Decor: Simply Elevated
          </p>
          <p className="mt-4 text-sm text-primary-foreground/60 max-w-sm leading-relaxed">
            Thoughtfully sourced home décor — serving trays, ceramics, cushion &
            sofa covers, pure-cotton bedding and table linen for the everyday
            elevated.
          </p>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.2em] text-accent mb-5">Explore</h4>
          <ul className="space-y-3 text-sm text-primary-foreground/80">
            <li><Link to="/shop" className="hover:text-accent transition-colors">Shop All</Link></li>
            <li><Link to="/quiz" className="hover:text-accent transition-colors">Style Quiz</Link></li>
            <li><Link to="/track" className="hover:text-accent transition-colors">Track Order</Link></li>
            <li><Link to="/contact" className="hover:text-accent transition-colors">Contact Us</Link></li>
            <li><Link to="/account" className="hover:text-accent transition-colors">My Account</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-xs uppercase tracking-[0.2em] text-accent mb-5">Get in touch</h4>
          <div className="space-y-4 text-sm">
            <a
              href={`https://wa.me/${WHATSAPP}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 text-primary-foreground/85 hover:text-accent transition-colors"
              data-testid="footer-whatsapp"
            >
              <MessageCircle size={18} /> WhatsApp: +91 95992 72633
            </a>
            <a
              href={INSTAGRAM}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 text-primary-foreground/85 hover:text-accent transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram size={18} /> instagram.com/aetheldecor
            </a>
            <a
              href="mailto:info@aetheldecor.com"
              className="flex items-center gap-3 text-primary-foreground/85 hover:text-accent transition-colors"
              data-testid="footer-email"
            >
              <Mail size={18} /> info@aetheldecor.com
            </a>
          </div>
          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.15em] text-primary-foreground/60 mb-1">Stay in the loop</p>
            <p className="text-xs text-primary-foreground/40 mb-2">Curated decor inspiration & exclusive offers.</p>
            <NewsletterForm />
          </div>
        </div>
      </div>
      <div className="border-t border-primary-foreground/15">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6 text-xs text-primary-foreground/50 flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} Aethel Decor. aetheldecor.com</span>
          <span>GST: 06AHFPV8578A2ZZ · Free shipping across India · COD available</span>
        </div>
      </div>
    </footer>
  );
}
